<template>
  <v-menu v-model="open" location="bottom start" :offset="4" :close-on-content-click="false">
    <template #activator="{ props: activatorProps }">
      <button
        v-bind="activatorProps"
        type="button"
        class="ctx-dd-trigger"
        :class="{ 'ctx-dd-trigger--highlight': highlight }"
        :disabled="loading"
        :aria-label="menuLabel"
      >
        <span class="ctx-dd-trigger__text" :class="{ 'ctx-placeholder': !selectedLabel }">
          {{ selectedLabel ?? placeholder }}
        </span>
      </button>
    </template>

    <v-list class="ctx-dd-menu" density="compact" role="listbox" :aria-label="menuLabel">
      <v-list-item
        class="ctx-dd-item"
        role="option"
        :aria-selected="modelValue === null"
        @click="pick(null, false)"
      >
        <v-list-item-title class="ctx-dd-item__label ctx-placeholder">{{ placeholder }}</v-list-item-title>
      </v-list-item>

      <template v-for="(section, sectionIndex) in sections" :key="sectionIndex">
        <v-list-subheader v-if="section.label" class="ctx-dd-subheader">{{ section.label }}</v-list-subheader>
        <!-- No `:title` on v-list-item: Vuetify treats it as the item's TITLE PROP and
             renders it as content. The native tooltip goes on the inner label instead. -->
        <v-list-item
          v-for="item in section.items"
          :key="item.id"
          class="ctx-dd-item"
          role="option"
          :class="{
            'ctx-dd-item--disabled': item.disabled,
            'ctx-dd-item--selected': item.id === modelValue,
            'ctx-dd-item--scope': item.scope,
          }"
          :aria-selected="item.id === modelValue"
          :aria-disabled="item.disabled || undefined"
          @click="pick(item.id, item.disabled)"
        >
          <v-list-item-title
            class="ctx-dd-item__label"
            :class="{ 'ctx-dd-item__label--pinned': item.pinned }"
            :title="item.disabled ? item.disabledReason : undefined"
          >
            {{ item.label }}
          </v-list-item-title>
          <template #append>
            <font-awesome-icon v-if="item.pinned" icon="thumbtack" class="ctx-dd-item__pin" />
          </template>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { ContextMenuSection } from '@/composables/context-menu-items';

/**
 * Compact dropdown for the context bar (#838). Replaces the native <select>: options
 * disabled by the context stay hoverable (so their tooltip can say WHY), pin-compatible
 * options carry a real thumbtack whose colour follows the theme, and the styling works
 * in every browser — none of which a native <option> can do.
 *
 * Disabled options deliberately avoid Vuetify's `disabled` prop: it sets
 * pointer-events: none, which would kill the explanatory tooltip. The click handler
 * guards instead, and `close-on-content-click` is off so a click on a disabled option
 * does not silently dismiss the menu.
 */

defineProps<{
  modelValue: string | null;
  /** Label of the current selection, rendered on the trigger. Null shows the placeholder. */
  selectedLabel: string | null;
  placeholder: string;
  menuLabel: string;
  sections: ContextMenuSection[];
  loading?: boolean;
  highlight?: boolean;
}>();

const emit = defineEmits<{ 'update:modelValue': [id: string | null] }>();

const open = ref(false);

function pick(id: string | null, disabled: boolean) {
  if (disabled) return;
  open.value = false;
  emit('update:modelValue', id);
}
</script>

<style scoped>
/* The trigger carries the exact box the native .ctx-select had (context-toolbar.vue),
   so the bar's geometry does not move: same height, chevron, radius, highlight pulse. */
.ctx-dd-trigger {
  display: inline-flex;
  align-items: center;
  text-align: left;
  height: 24px;
  /* Room on the right for the chevron drawn via background-image. */
  padding: 0 22px 0 9px;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
  /* Down-chevron so the control reads as a dropdown. */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235b7089' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  min-width: 110px;
  max-width: 160px;
  appearance: none;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}

.ctx-dd-trigger:hover:not(:disabled) {
  border-color: rgba(var(--v-theme-primary), 0.4);
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.ctx-dd-trigger:focus-visible {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.15);
}

.ctx-dd-trigger:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.ctx-dd-trigger--highlight {
  animation: ctx-dd-pulse 1.2s ease-in-out infinite;
}

@keyframes ctx-dd-pulse {

  0%,
  100% {
    border-color: rgba(var(--v-theme-primary), 0.22);
    box-shadow: none;
    background-color: rgb(var(--v-theme-surface));
  }

  50% {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.35), 0 0 10px rgba(var(--v-theme-primary), 0.25);
    background-color: rgba(var(--v-theme-primary), 0.1);
  }
}

/* Below the md breakpoint the toolbar stacks its controls full-width (see
   context-toolbar.vue's media block, which sizes .ctx-item). */
@media (max-width: 959px) {
  .ctx-dd-trigger {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
    width: 100%;
    height: 34px;
    font-size: 14px;
  }
}

.ctx-dd-trigger__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* The italic placeholder's last glyph overhangs its box and overflow:hidden
     truncates it; a sliver of padding keeps the slant inside. */
  padding-right: 3px;
}

.ctx-dd-menu {
  min-width: 190px;
  max-width: 300px;
  max-height: min(420px, 70vh);
  overflow-y: auto;
  padding: 4px;
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20, 55, 90, 0.12);
  scrollbar-color: rgba(var(--v-theme-primary), 0.32) transparent;
  scrollbar-width: thin;
}

.ctx-dd-menu :deep(.v-list-item) {
  min-height: 28px;
  padding-inline: 10px;
  border-radius: 6px;
}

.ctx-dd-subheader {
  min-height: 24px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-muted);
}

.ctx-dd-item__label {
  font-size: 12px;
  font-weight: 500;
}

/* Compatible with the active pin: label and thumbtack share the primary blue. */
.ctx-dd-item__label--pinned {
  color: rgb(var(--v-theme-primary));
}

.ctx-dd-item__pin {
  font-size: 10px;
  color: rgb(var(--v-theme-primary));
}

.ctx-dd-item--selected {
  background: rgba(var(--v-theme-primary), 0.1);
}

/* A family entry — « Tous les programmes », « Groupes », « Individualisés » (#948). It stands
   for every line under it, so it must not look like one of them: tinted ground, heavier label.
   These entries took the place of the section subheaders, which were not clickable; keeping the
   subheader's weight is what makes the menu read the same way it did. */
.ctx-dd-item--scope {
  background: rgba(var(--v-theme-primary), 0.06);
  margin-top: 2px;
}

.ctx-dd-item--scope:first-child {
  margin-top: 0;
}

.ctx-dd-item--scope .ctx-dd-item__label {
  font-weight: 700;
}

/* The selection wins over the family tint: one has to be able to see which is chosen. */
.ctx-dd-item--scope.ctx-dd-item--selected {
  background: rgba(var(--v-theme-primary), 0.18);
}

.ctx-dd-item--disabled {
  cursor: default;
}

.ctx-dd-item--disabled :deep(.v-list-item-title) {
  color: var(--color-text-faint);
  font-style: italic;
}

.ctx-placeholder {
  font-style: italic;
  color: var(--color-text-faint);
}
</style>
